import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { SpacetimeDBProvider, useSpacetimeDB } from 'spacetimedb/react';
import { DbConnection } from '@vidmakr/bindings';
import { useSession } from './session';

let current: DbConnection | null = null;
const listeners = new Set<(c: DbConnection | null) => void>();

/** The live SpacetimeDB connection (for imperative use outside React). */
export function conn(): DbConnection {
  if (!current) throw new Error('Not connected');
  return current;
}

export function maybeConn(): DbConnection | null {
  return current;
}

/** Reducers of the live connection. */
export function reducers() {
  return conn().reducers;
}

export function useConnection(): DbConnection | null {
  const [c, setC] = useState(current);
  useEffect(() => {
    listeners.add(setC);
    setC(current);
    return () => void listeners.delete(setC);
  }, []);
  return c;
}

export function StdbProvider({ children }: { children: ReactNode }) {
  const spacetime = useSession((s) => s.spacetime);
  const builder = useMemo(() => {
    if (!spacetime) return null;
    const s = useSession.getState();
    return DbConnection.builder()
      .withUri(spacetime.uri)
      .withDatabaseName(spacetime.database)
      .withToken(s.token)
      .withTokenProvider(() => useSession.getState().refreshToken())
      .withLightMode(false)
      .onConnect((c) => {
        current = c as DbConnection;
        for (const l of listeners) l(current);
      })
      .onDisconnect(() => {
        for (const l of listeners) l(current);
      });
  }, [spacetime]);
  if (!builder) return null;
  return <SpacetimeDBProvider connectionBuilder={builder}>{children}</SpacetimeDBProvider>;
}

export function useStdbStatus() {
  const { isActive } = useSpacetimeDB();
  return isActive;
}
